// @ts-nocheck
import React, { useState, useMemo } from 'react';
import { trpcClient } from '@/lib/trpc';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Combobox } from '@/components/ui/combobox';
import { Link, useParams, useRouterState } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { exportInventoryNXTReport } from '@/lib/excelReportEngine';
import { toast } from 'sonner';
import { Calendar, Download, Eye, Printer, Layers, RefreshCw } from 'lucide-react';
import { ListPageShell } from '@/components/shared/ListPageShell';
import { CompactKpiRibbon, type CompactKpiItem } from '@/components/shared/CompactKpiRibbon';
import { Button } from '@/components/ui/button';
import { ExportImportButtons } from '@/components/shared/ExportImportButtons';
import { InlineFilterRow } from '@/components/shared/InlineFilterRow';
import type { FilterField } from '@/components/shared/FilterBar';
import { cn, formatNumber, viIncludes } from '@/lib/utils';
import { fromPrimaryQty, formatUnitQty } from '@apv-erp/status';
import { toVnDateString, vnStartOfMonth, resolveWarehouseDomain, warehouseDomainOf, WAREHOUSE_DOMAINS } from "@apv-erp/status";
import PrintDialog from '@/components/shared/PrintDialog';
import { InventoryNxtPrintTemplate } from '@/components/warehouse/print/InventoryReportPrintTemplates';
import { emsTabClass } from "@/components/ui/ems-tabs";
import { usePageSizeState } from "@/hooks/usePreferredPageSize";

const FILTER_FIELDS: FilterField[] = [
  { key: 'code', label: 'Mã VT / TP', type: 'text', placeholder: 'Mã...' },
  { key: 'name', label: 'Tên VT / TP', type: 'text', placeholder: 'Tên...' },
  { key: 'unit', label: 'ĐVT', type: 'text', placeholder: 'ĐVT...' },
  { key: 'warehouseName', label: 'Kho', type: 'text', placeholder: 'Kho...' },
];

/** Kho quản lý theo tổng số lượng: gộp các dòng cùng kho + mặt hàng (không tách theo lô) */
function mergeByItem(rows: any[]) {
  const map = new Map<string, any>();
  for (const r of rows) {
    const key = `${r.warehouseId || r.warehouse?.id || r.warehouseName || ''}|${r.itemType || ''}|${r.itemId || r.itemCode || ''}`;
    const cur = map.get(key);
    if (!cur) {
      map.set(key, { ...r, lotNumber: undefined, expiryDate: undefined });
      continue;
    }
    cur.openingBalance = Number(cur.openingBalance || 0) + Number(r.openingBalance || 0);
    cur.inQuantity = Number(cur.inQuantity || 0) + Number(r.inQuantity || 0);
    cur.outQuantity = Number(cur.outQuantity || 0) + Number(r.outQuantity || 0);
    cur.closingBalance = Number(cur.closingBalance || 0) + Number(r.closingBalance || 0);
  }
  return Array.from(map.values());
}

/** Lọc dòng báo cáo theo ô tìm kiếm đầu trang (?q=) + bộ lọc từng cột; dùng chung cho bảng và file Excel */
function applyNxtFilters(items: any[], opts: { showZeroBalance: boolean; urlQ: string; filterValues: Record<string, any> }) {
  const { showZeroBalance, urlQ, filterValues } = opts;
  let list = items.filter((item: any) =>
    showZeroBalance ? true : (item.openingBalance !== 0 || item.inQuantity !== 0 || item.outQuantity !== 0 || item.closingBalance !== 0)
  );
  const codeOf = (item: any) => item.itemCode || item.material?.code || item.product?.code || item.material?.materialId || '';
  const nameOf = (item: any) => item.itemName || item.material?.name || item.product?.name || '';
  if (urlQ) {
    list = list.filter((item: any) => viIncludes(codeOf(item), urlQ) || viIncludes(nameOf(item), urlQ));
  }
  const textFilter = (key: string, pick: (item: any) => string) => {
    if (!filterValues[key]) return;
    const q = String(filterValues[key]).toLowerCase();
    list = list.filter((item: any) => (pick(item) || '').toLowerCase().includes(q));
  };
  textFilter('code', codeOf);
  textFilter('name', nameOf);
  textFilter('unit', (item) => item.unit || item.material?.unit || item.product?.unit || '');
  textFilter('warehouseName', (item) => item.warehouseName || item.warehouse?.name || '');
  return list;
}

export default function InventoryNXTPage() {
  const { warehouseType } = useParams({ strict: false }) as any;
  const [showZeroBalance, setShowZeroBalance] = useState(false);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = usePageSizeState(25);
  const [showFilter, setShowFilter] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});

  const handleFilterChange = (key: string, value: any) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleClearAll = () => {
    setFilterValues({});
    setCurrentPage(1);
  };

  const [startDate, setStartDate] = useState(() => vnStartOfMonth());
  const [endDate, setEndDate] = useState(() => toVnDateString());

  // Read search keyword directly from Header Contextual Search (?q=)
  const routerSearch = useRouterState({ select: (s) => s.location.search }) as Record<string, any>;
  const urlQ: string = (routerSearch?.q || '').trim();

  // Load Warehouses for Dropdown
  const { data: warehouseData } = useQuery({
    queryKey: ['warehouses', 'list'],
    queryFn: () => trpcClient.warehouses.list.query({ limit: 100 })
  });
  // Trang của một nhóm kho (vật tư / thành phẩm / kỹ thuật / BTP / phế liệu): chỉ chọn kho đúng nhóm
  const domain = warehouseType ? resolveWarehouseDomain(warehouseType) : null;
  const domainLabel = domain ? WAREHOUSE_DOMAINS[domain].label : 'Tất cả kho';
  const warehouses = (warehouseData?.items || []).filter((w: any) => {
    if (!domain) return true;
    const d = warehouseDomainOf(w.type);
    return d === domain || d === null;
  });
  const [printOpen, setPrintOpen] = useState(false);

  // Ngày gửi dạng YYYY-MM-DD: máy chủ hiểu là trọn ngày theo giờ Việt Nam (FB-JMLIL2)
  const reportInput = {
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    itemType: warehouseType ? warehouseType.toUpperCase() : 'ALL',
    warehouseId: selectedWarehouseId === 'ALL' ? undefined : selectedWarehouseId,
    // Máy chủ trả cả mặt hàng tồn = 0 và hàng trong danh mục kho chưa nhập xuất khi bật "Hiện Tồn = 0"
    showZeroBalance,
  };
  const { data: rawData, isLoading } = useQuery({
    queryKey: ['inventory', 'reportNXT', reportInput],
    queryFn: () => trpcClient.inventory.reportNXT.query(reportInput),
  });
  const data = rawData as any;

  // Filter raw data by Zero Balance, Header Search query (?q=), and column filters
  const filteredData = useMemo(
    () => applyNxtFilters(mergeByItem(data?.data || []), { showZeroBalance, urlQ, filterValues }),
    [data?.data, showZeroBalance, urlQ, filterValues],
  );

  // Paginate filtered data
  const totalItems = filteredData.length;
  const pagedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  // 4 Colored KPI Summary Tags
  const summaryKpiItems: CompactKpiItem[] = useMemo(() => [
    {
      key: 'beginning',
      label: 'Tồn Đầu Kỳ',
      count: formatNumber(data?.summary?.beginningBalance || 0),
      variant: 'slate',
      clickable: false,
    },
    {
      key: 'in',
      label: 'Tổng Nhập',
      count: `+${formatNumber(data?.summary?.inQuantity || 0)}`,
      variant: 'emerald',
      clickable: false,
    },
    {
      key: 'out',
      label: 'Tổng Xuất',
      count: `-${formatNumber(data?.summary?.outQuantity || 0)}`,
      variant: 'rose',
      clickable: false,
    },
    {
      key: 'ending',
      label: 'Tồn Cuối Kỳ',
      count: formatNumber(data?.summary?.endingBalance || 0),
      variant: 'blue',
      clickable: false,
    },
  ], [data?.summary]);

  const queryClient = useQueryClient();
  const reconcileLedgersMutation = useMutation({
    mutationFn: async () => {
      return (trpcClient as any).inventory.reconcileLedgers.mutate();
    },
    onSuccess: (result: any) => {
      toast.success(result?.message || "Đã kiểm toán thành công sổ cái");
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
    onError: (err: any) => {
      toast.error(err?.message || "Lỗi kiểm toán sổ cái");
    },
  });

  const handleReconcile = () => {
    if (window.confirm("Bạn có chắc chắn muốn chạy lại kiểm toán toàn bộ sổ kho, công nợ và khuôn bế? Quá trình này có thể mất vài giây.")) {
      reconcileLedgersMutation.mutate();
    }
  };

  // Xuất Excel: lấy lại toàn bộ dòng từ máy chủ (không phụ thuộc trang đang xem), áp cùng bộ lọc
  const handleExport = async () => {
    const toastId = toast.loading('Đang khởi tạo file Excel Sổ kho NXT chuẩn ISO...');
    try {
      const fresh: any = await trpcClient.inventory.reportNXT.query(reportInput);
      const rows = applyNxtFilters(mergeByItem(fresh?.data || []), { showZeroBalance, urlQ, filterValues });
      if (rows.length === 0) {
        toast.dismiss(toastId);
        toast.warning('Không có dữ liệu tồn kho để xuất.');
        return;
      }
      const formatted = rows.map((item: any) => ({
        itemCode: item.itemCode || item.material?.code || item.product?.code || item.itemId,
        itemName: item.itemName || item.material?.name || item.product?.name || '—',
        itemType: item.itemType || warehouseType || 'MATERIAL',
        warehouseName: item.warehouseName || item.warehouse?.name || '',
        unit: item.unit || item.material?.unit || item.product?.unit || '-',
        openingQty: Number(item.openingBalance || 0),
        inQty: Number(item.inQuantity || 0),
        outQty: Number(item.outQuantity || 0),
        closingQty: Number(item.closingBalance || 0),
        unitPrice: Number(item.material?.unitPrice || 0),
      }));

      const currentWhName = selectedWarehouseId === 'ALL'
        ? 'Tất cả các kho'
        : (warehouses.find((w: any) => w.id === selectedWarehouseId)?.name || warehouseType || 'Kho Doanh Nghiệp');

      await exportInventoryNXTReport({
        periodText: `Từ ${startDate} đến ${endDate}`,
        warehouseName: currentWhName,
        items: formatted,
      });
      toast.dismiss(toastId);
      toast.success('Xuất file Excel Sổ kho thành công!');
    } catch (err: any) {
      toast.dismiss(toastId);
      toast.error(`Lỗi xuất file Excel: ${err.message}`);
    }
  };

  return (
    <ListPageShell
      currentPage={currentPage}
      pageSize={pageSize}
      totalItems={totalItems}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => {
        setPageSize(size);
        setCurrentPage(1);
      }}
      isLoading={isLoading}
      showFilter={showFilter}
      onToggleFilter={() => setShowFilter((prev) => !prev)}
      onClearAll={handleClearAll}
      filterFields={FILTER_FIELDS}
      filterValues={filterValues}
      tabs={
        <div className="flex items-center gap-4">
          <button
            type="button"
            className={emsTabClass(true, { size: "sm" })}
          >
            <Layers className="w-3.5 h-3.5 text-indigo-600" />
            <span>Báo cáo NXT</span>
            <span className="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-primary/10 text-primary tabular-nums">
              {totalItems}
            </span>
          </button>
        </div>
      }
      headerFilters={
        <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto no-scrollbar py-0.5 min-w-0">
          {/* Compact Warehouse Selector */}
          <div className="shrink-0 w-44">
            <Combobox
              options={[
                { label: 'Tất cả kho', value: 'ALL' },
                ...warehouses.map((w: any) => ({
                  label: w.name,
                  value: w.id,
                }))
              ]}
              value={selectedWarehouseId}
              onChange={(val) => {
                setSelectedWarehouseId(val || 'ALL');
                setCurrentPage(1);
              }}
              placeholder="Chọn kho..."
              searchPlaceholder="Tìm kho..."
              emptyText="Không tìm thấy kho"
              className="h-7 text-xs"
            />
          </div>

          {/* Switch "Hiện Tồn = 0" */}
          <div className="flex items-center gap-1.5 px-2 py-0.5 bg-slate-100/70 rounded border border-slate-200/80 shrink-0">
            <Switch
              id="zero-balance"
              checked={showZeroBalance}
              onCheckedChange={(val) => {
                setShowZeroBalance(val);
                setCurrentPage(1);
              }}
              className="scale-75"
            />
            <Label htmlFor="zero-balance" className="text-[11px] cursor-pointer text-slate-600 whitespace-nowrap font-medium">
              Hiện Tồn = 0
            </Label>
          </div>

          {/* 4 Colored KPI Summary Tags */}
          <CompactKpiRibbon
            items={summaryKpiItems}
            className="h-7 py-0 shrink-0"
            testIdPrefix="nxt-kpi"
          />
        </div>
      }
      dateFilter={
        <div className="flex items-center gap-1 bg-slate-100/90 px-2 py-0.5 rounded border border-slate-200 shrink-0 text-xs">
          <Calendar className="w-3.5 h-3.5 text-slate-500 shrink-0" />
          <Input
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setCurrentPage(1);
            }}
            className="h-6 w-28 text-[11px] p-0.5 border-0 bg-transparent focus-visible:ring-0"
          />
          <span className="text-slate-400">-</span>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setCurrentPage(1);
            }}
            className="h-6 w-28 text-[11px] p-0.5 border-0 bg-transparent focus-visible:ring-0"
          />
        </div>
      }
      actions={ 
        <>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 text-blue-600 border-blue-200 hover:bg-blue-50 hover:text-blue-700 bg-white shadow-sm"
          onClick={handleReconcile}
          disabled={reconcileLedgersMutation.isPending}
        >
          <RefreshCw className={cn("w-3.5 h-3.5", reconcileLedgersMutation.isPending && "animate-spin")} />
          <span className="hidden sm:inline">Kiểm toán Sổ</span>
        </Button>

        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 bg-white shadow-sm"
          onClick={() => setPrintOpen(true)}
          disabled={filteredData.length === 0}
          data-testid="nxt-print-btn"
        >
          <Printer className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">In / PDF</span>
        </Button>

        <ExportImportButtons
          onExport={handleExport}
          exportOnly
        />
        </>
      }
    >
      {printOpen && (
        <PrintDialog
          open={printOpen}
          onClose={() => setPrintOpen(false)}
          type="custom"
          orientation="landscape"
          title="Báo cáo Nhập - Xuất - Tồn"
          documentTitle={`BaoCaoNXT_${domain ? WAREHOUSE_DOMAINS[domain].slug : 'tat-ca'}_${startDate}_${endDate}`}
        >
          <InventoryNxtPrintTemplate
            title={`BÁO CÁO NHẬP - XUẤT - TỒN ${domainLabel.toUpperCase()}`}
            warehouseLabel={
              selectedWarehouseId === 'ALL'
                ? `${domainLabel}${domain ? ' — tất cả kho' : ''}`
                : warehouses.find((w: any) => w.id === selectedWarehouseId)?.name || domainLabel
            }
            startDate={startDate}
            endDate={endDate}
            rows={filteredData.map((item: any) => ({
              itemCode: item.itemCode || item.material?.code || item.product?.code || item.itemId,
              itemName: item.itemName || item.material?.name || item.product?.name || '—',
              unit: item.unit || item.material?.unit || item.product?.unit || '',
              warehouseName: item.warehouseName || item.warehouse?.name || '',
              lotNumber: item.lotNumber,
              opening: Number(item.openingBalance || 0),
              inQty: Number(item.inQuantity || 0),
              outQty: Number(item.outQuantity || 0),
              closing: Number(item.closingBalance || 0),
            }))}
          />
        </PrintDialog>
      )}
      <table className="w-full text-xs caption-bottom apv-compact-table min-w-[1050px]">
        <thead className="apv-table-header">
          <tr>
            <th className="w-[45px] text-center font-bold text-slate-700 whitespace-nowrap">STT</th>
            <th className="w-[140px] text-left font-bold text-slate-700 whitespace-nowrap">Mã VT / TP</th>
            <th className="min-w-[200px] text-left font-bold text-slate-700 whitespace-nowrap">Tên VT / TP</th>
            <th className="w-[65px] text-center font-bold text-slate-700 whitespace-nowrap">ĐVT</th>
            <th className="w-[130px] text-left font-bold text-slate-700 whitespace-nowrap">Kho</th>
            <th className="w-[105px] text-right font-bold text-slate-700 whitespace-nowrap">Tồn đầu kỳ</th>
            <th className="w-[105px] text-right font-bold text-emerald-700 whitespace-nowrap">Tổng nhập</th>
            <th className="w-[105px] text-right font-bold text-rose-700 whitespace-nowrap">Tổng xuất</th>
            <th className="w-[115px] text-right font-bold text-blue-700 whitespace-nowrap">Tồn cuối kỳ</th>
            <th className="w-[110px] text-right pr-3 font-bold text-slate-700 whitespace-nowrap">Thao tác</th>
          </tr>
          {showFilter && (
            <InlineFilterRow
              columns={[
                null,
                { key: 'code' },
                { key: 'name' },
                { key: 'unit' },
                { key: 'warehouseName' },
                null,
                null,
                null,
                null,
                null,
              ]}
              fields={FILTER_FIELDS}
              values={filterValues}
              onChange={handleFilterChange}
            />
          )}
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={10} className="h-48 text-center text-slate-400">
                Đang tải dữ liệu báo cáo tồn kho...
              </td>
            </tr>
          ) : pagedItems.length === 0 ? (
            <tr>
              <td colSpan={10} className="h-48 text-center text-slate-400">
                Không tìm thấy dữ liệu tồn kho phù hợp với điều kiện lọc.
              </td>
            </tr>
          ) : (
            pagedItems.map((item: any, idx: number) => {
              const stt = (currentPage - 1) * pageSize + idx + 1;
              const code =
                item.material?.materialId ||
                item.material?.code ||
                item.product?.productCode ||
                item.product?.code ||
                item.itemCode ||
                item.code ||
                '—';
              const name =
                item.material?.name ||
                item.product?.productName ||
                item.product?.name ||
                item.itemName ||
                item.name ||
                '—';
              const unit =
                item.material?.unit ||
                item.product?.unit ||
                item.unit ||
                '—';
              const whName = item.warehouseName || item.warehouse?.name || '—';

              return (
                <tr
                  key={item.id || `${item.itemId}-${item.warehouseId}-${idx}`}
                  className="hover:bg-slate-50/80 transition-colors border-b border-slate-100 h-8"
                >
                  <td className="text-center font-mono text-[11px] text-slate-500">{stt}</td>
                  <td className="font-mono font-medium text-slate-900">{code}</td>
                  <td className="font-medium text-slate-800 truncate max-w-[260px]" title={name}>
                    {name}
                  </td>
                  <td className="text-center text-slate-600">{unit}</td>
                  <td className="text-slate-600 truncate max-w-[140px]" title={whName}>
                    {whName}
                  </td>
                  <td className="text-right font-mono font-medium text-slate-700">
                    {formatNumber(item.openingBalance || 0)}
                  </td>
                  <td className="text-right font-mono font-medium text-emerald-600">
                    {formatNumber(item.inQuantity || 0)}
                  </td>
                  <td className="text-right font-mono font-medium text-rose-600">
                    {formatNumber(item.outQuantity || 0)}
                  </td>
                  <td className="text-right font-mono font-bold text-blue-700">
                    {formatNumber(item.closingBalance || 0)}
                    {/* Tồn cuối quy đổi ĐVT phụ (04/11/2026) */}
                    {(() => {
                      const sec = item.material?.secondaryUnit ? fromPrimaryQty(Number(item.closingBalance || 0), item.material) : null;
                      return sec !== null ? (
                        <div className="text-[11px] font-normal text-slate-500" data-testid="nxt-closing-secondary">
                          {formatUnitQty(sec)} {item.material.secondaryUnit}
                        </div>
                      ) : null;
                    })()}
                  </td>
                  <td className="text-right pr-3">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/warehouse/${warehouseType || 'material'}/stock-card` as any}
                        search={{
                          itemId: item.itemId,
                          type: item.itemType || warehouseType || 'MATERIAL',
                          warehouseId: item.warehouseId,
                        } as any}
                        className="text-indigo-600 hover:text-indigo-700 hover:underline text-[11px] font-medium"
                      >
                        Thẻ Kho
                      </Link>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </ListPageShell>
  );
}
